// =============================================================================
// CHAT STORE (Zustand)
// =============================================================================

import { create } from 'zustand';
import type { LinkPreviewData } from '@chlann/shared';
import { api } from '../api/client.js';
import { socketService } from '../api/socket.js';

// =============================================================================
// TYPES
// =============================================================================

export interface MessageAttachment {
  id: string;
  type: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  thumbnailUrl: string | null;
  url: string;
  width?: number;
  height?: number;
  durationSeconds?: number;
}

export interface MessageReaction {
  emoji: string;
  userIds: string[];
  count: number;
}

export type DeliveryStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

export interface ReadReceipt {
  userId: string;
  userName: string;
  readAt: string;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderDeviceId?: string | null; // For Signal Protocol decryption
  senderName: string;
  senderAvatarUrl: string | null;
  messageType: string;
  replyToMessageId: string | null;
  replyToPreview: {
    senderId: string;
    senderName: string;
    messageType: string;
    // Decrypted snippet of the quoted text message, null for media/undecryptable
    content?: string | null;
  } | null;
  hasAttachments: boolean;
  isForwarded: boolean;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  expiresAt: string | null;
  attachments: MessageAttachment[];
  reactions: MessageReaction[];
  // Plaintext content for bot messages, null/undefined for encrypted user messages
  content?: string | null;
  // Delivery status for own messages
  deliveryStatus?: DeliveryStatus;
  // Read receipts for direct messages
  readReceipts?: ReadReceipt[];
  // Server-unfurled link preview (present when the body carried an unfurlable URL).
  linkPreview?: LinkPreviewData;
}

export interface ConversationMember {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  role?: string;
  joinedAt?: string;
  // True when this member is a bot. The chat view uses this to label bot
  // participants and to disclose that a bot in the conversation can read
  // the messages posted there.
  isBot?: boolean;
}

export interface Conversation {
  id: string;
  type: string;
  name: string | null;
  avatarUrl: string | null;
  lastMessage: {
    id: string;
    senderId: string;
    senderName: string;
    content: string;
    messageType: string;
    createdAt: string;
    // Carried from the underlying Message so the list row can show a failed
    // own-send instead of silently reading as sent. Only 'failed' is rendered;
    // 'sending'/'sent'/undefined all read as a normal preview.
    deliveryStatus?: DeliveryStatus;
  } | null;
  unreadCount: number;
  isPinned: boolean;
  isMuted: boolean;
  mutedUntil: string | null;
  updatedAt: string;
  members: ConversationMember[];
}

export type MuteDuration = 'forever' | '8h' | '1d' | '7d' | null;

interface TypingUser {
  userId: string;
  userName: string;
}

// Track server IDs of messages we sent optimistically, so the socket echo is skipped
const ownSentMessageIds = new Set<string>();

// Full-refetch coalescing (see fetchMessages): one in-flight request per
// conversation, plus a marker for a single trailing rerun. Self-cleaning via
// the .finally() in fetchMessages.
const inflightRefetches = new Map<string, Promise<void>>();
const pendingReruns = new Set<string>();

interface ChatState {
  // State
  conversations: Conversation[];
  activeConversationId: string | null;
  messages: Record<string, Message[]>; // conversationId -> messages
  isLoadingConversations: boolean;
  isLoadingMessages: boolean;
  hasMoreMessages: Record<string, boolean>;
  error: string | null;
  typingUsers: Record<string, TypingUser[]>; // conversationId -> typing users
  onlineUsers: Set<string>; // Set of online user IDs
  highlightedMessageId: string | null;

  // Actions
  fetchConversations: () => Promise<void>;
  setActiveConversation: (id: string | null) => void;
  fetchMessages: (conversationId: string, loadMore?: boolean) => Promise<void>;
  addMessage: (message: Message) => void;
  confirmMessage: (tempId: string, serverId: string) => void;
  // Set the delivery status on a conversation's list preview, but only when that
  // preview still points at the given message (id guard) so a superseded send
  // never re-marks a newer preview. updateMessage only touches state.messages,
  // so the list row needs this separate hook to reflect a failed own-send.
  setLastMessageStatus: (conversationId: string, messageId: string, status: DeliveryStatus) => void;
  // After a contentless inbound broadcast, copy the server-decrypted body back
  // onto the conversation's list preview once the thread refetch has run.
  // Guarded on lastMessage.id so a newer preview is not clobbered. Mirrors
  // setLastMessageStatus.
  syncLastMessagePreview: (conversationId: string, messageId: string) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  removeMessage: (messageId: string, conversationId: string) => void;
  updateMessageReactions: (messageId: string, reactions: MessageReaction[]) => void;
  handleReactionEvent: (data: { messageId: string; conversationId: string; userId: string; emoji: string; action: 'add' | 'remove' }) => void;
  addReaction: (messageId: string, emoji: string) => Promise<boolean>;
  removeReaction: (messageId: string, emoji: string) => Promise<boolean>;
  createConversation: (type: 'direct' | 'group', memberIds: string[], name?: string) => Promise<string | null>;
  pinConversation: (conversationId: string, pinned: boolean) => Promise<void>;
  muteConversation: (conversationId: string, duration: MuteDuration) => Promise<void>;
  editMessageAction: (messageId: string, newContent: string) => Promise<boolean>;
  deleteMessageAction: (messageId: string, forEveryone: boolean) => Promise<boolean>;
  clearError: () => void;

  // Typing indicators
  setUserTyping: (conversationId: string, userId: string, userName: string) => void;
  setUserStoppedTyping: (conversationId: string, userId: string) => void;

  // Online status
  setUserOnline: (userId: string) => void;
  setUserOffline: (userId: string) => void;

  // Read receipts
  updateDeliveryStatus: (messageId: string, status: DeliveryStatus) => void;
  addReadReceipt: (messageId: string, receipt: ReadReceipt) => void;
  markMessagesAsRead: (conversationId: string, messageIds: string[]) => Promise<void>;

  // Search highlight
  setHighlightedMessage: (messageId: string | null) => void;

  // Reset store (call on logout)
  reset: () => void;

  // Socket event setup
  setupSocketListeners: () => () => void;
}

// =============================================================================
// STORE
// =============================================================================

export const useChatStore = create<ChatState>((set, get) => ({
  // Initial state
  conversations: [],
  activeConversationId: null,
  messages: {},
  highlightedMessageId: null,
  isLoadingConversations: false,
  isLoadingMessages: false,
  hasMoreMessages: {},
  error: null,
  typingUsers: {},
  onlineUsers: new Set<string>(),

  // Fetch all conversations
  fetchConversations: async () => {
    set({ isLoadingConversations: true, error: null });

    const result = await api.getConversations();

    if (result.error) {
      set({ isLoadingConversations: false, error: result.error.message });
      return;
    }

    if (result.data) {
      set({
        isLoadingConversations: false,
        conversations: result.data.conversations,
      });
    }
  },

  // Set active conversation. Always refetch on activation so re-entering the
  // route after navigating away picks up messages that arrived while the
  // chat view was unmounted (and the socket room was left).
  setActiveConversation: (id) => {
    set({ activeConversationId: id });
    if (id) {
      void get().fetchMessages(id);
    }
  },

  // Fetch messages for a conversation
  fetchMessages: async (conversationId, loadMore = false) => {
    // Coalesce concurrent full refetches per conversation. message:new and
    // message:edited heals can fire in bursts, and each full fetch is the
    // most expensive authed call (server-side decrypt). A request that
    // arrives while one is in flight marks a single trailing rerun instead
    // of stacking a parallel fetch - the rerun STARTS after the current one
    // finishes, so data that landed mid-flight is still picked up.
    // loadMore pages are cursor-scoped and never coalesced.
    if (!loadMore) {
      const inflight = inflightRefetches.get(conversationId);
      if (inflight) {
        pendingReruns.add(conversationId);
        return inflight;
      }
    }

    const doFetch = async (): Promise<void> => {
      set({ isLoadingMessages: true, error: null });

      const existingMessages = get().messages[conversationId] || [];
      const before = loadMore && existingMessages.length > 0
        ? existingMessages[existingMessages.length - 1].id
        : undefined;

      const result = await api.getMessages(conversationId, { before, limit: 50 });

      if (result.error) {
        set({ isLoadingMessages: false, error: result.error.message });
        return;
      }

      if (result.data) {
        // ADR-012 P6: the server is the keyholder. getMessages already returns
        // decrypted `content` for server-scheme ciphertext (after the server's
        // membership check), or null for an undecryptable pre-cut Signal row -
        // which MessageBubble renders as the honest pre-cut placeholder. No
        // client-side decrypt, no per-message GET /content, no local cache.
        const newMessages = result.data.messages as Message[];

        // Functional update against the CURRENT state. The previous code
        // spread a snapshot taken before the await, so anything written
        // while the request was in flight - a socket message landing in
        // another conversation, a parallel fetch resolving - was wiped
        // ("messages arrive then vanish").
        set((current) => {
          const currentList = current.messages[conversationId] || [];
          let nextList: Message[];
          if (loadMore) {
            // Drop rows already present (a full refetch may have resolved
            // mid-flight and overlap this page) so React keys stay unique.
            const seen = new Set(currentList.map((m) => m.id));
            nextList = [...currentList, ...newMessages.filter((m) => !seen.has(m.id))];
          } else {
            nextList = newMessages;
          }
          return {
            isLoadingMessages: false,
            messages: {
              ...current.messages,
              [conversationId]: nextList,
            },
            hasMoreMessages: {
              ...current.hasMoreMessages,
              [conversationId]: result.data!.hasMore,
            },
          };
        });
      }
    };

    if (loadMore) {
      return doFetch();
    }

    const run = doFetch().finally(() => {
      inflightRefetches.delete(conversationId);
      if (pendingReruns.delete(conversationId)) {
        void get().fetchMessages(conversationId);
      }
    });
    inflightRefetches.set(conversationId, run);
    return run;
  },

  // Add a new message (from socket or send)
  addMessage: (message) => {
    const state = get();
    const conversationId = message.conversationId;
    const existingMessages = state.messages[conversationId] || [];

    // Skip if this is our own message echoed back from the server
    if (ownSentMessageIds.has(message.id)) {
      ownSentMessageIds.delete(message.id);
      return;
    }

    // Skip WebSocket echo that arrives before API response confirms the server ID.
    // If we have a pending optimistic message (deliveryStatus: 'sending') from the
    // same sender in this conversation, the incoming message is the echo.
    const pendingOptimistic = existingMessages.find(
      (m) => m.senderId === message.senderId && m.deliveryStatus === 'sending'
    );
    if (pendingOptimistic) {
      ownSentMessageIds.add(message.id);
      return;
    }

    // Add message at the beginning (newest first)
    set({
      messages: {
        ...state.messages,
        [conversationId]: [message, ...existingMessages],
      },
    });

    // A fresh own send carries deliveryStatus 'sending' (socket echoes and
    // inbound messages never do), so it uniquely identifies the current user as
    // the sender here. Sending marks the conversation read: a prior inbound must
    // not keep counting as unread once you have replied (#794).
    const isOwnSend = message.deliveryStatus === 'sending';

    // Update conversation's last message; an own send also zeroes the unread
    // badge immediately (optimistic), independent of the server round-trip.
    const conversations = state.conversations.map((c) =>
      c.id === conversationId
        ? {
            ...c,
            lastMessage: {
              id: message.id,
              senderId: message.senderId,
              senderName: message.senderName,
              content: message.content || '',
              messageType: message.messageType,
              createdAt: message.createdAt,
              deliveryStatus: message.deliveryStatus,
            },
            updatedAt: message.createdAt,
            ...(isOwnSend ? { unreadCount: 0 } : {}),
          }
        : c
    );

    set({ conversations });

    // Advance the server read pointer past any loaded inbound messages so the
    // cleared badge does not reappear on the next getConversations refetch.
    if (isOwnSend) {
      const inboundIds = existingMessages
        .filter((m) => m.senderId !== message.senderId)
        .map((m) => m.id);
      if (inboundIds.length > 0) {
        void get().markMessagesAsRead(conversationId, inboundIds);
      }
    }
  },

  // Replace temp message ID with server ID after successful send
  confirmMessage: (tempId, serverId) => {
    ownSentMessageIds.add(serverId);
    get().updateMessage(tempId, { id: serverId, deliveryStatus: 'sent' });
  },

  // Reflect a send's delivery status on the conversation's list preview. Guarded
  // on lastMessage.id so a stale failure (a newer message has since become the
  // preview) is ignored rather than mis-marking the row.
  setLastMessageStatus: (conversationId, messageId, status) => {
    const state = get();
    set({
      conversations: state.conversations.map((c) =>
        c.id === conversationId && c.lastMessage?.id === messageId
          ? { ...c, lastMessage: { ...c.lastMessage, deliveryStatus: status } }
          : c
      ),
    });
  },

  // Heal the list preview after a `message:new` broadcast that carried no
  // content (the default `server` crypto mode broadcasts metadata only, so
  // addMessage stored ''). The thread refetch fills state.messages with the
  // server-decrypted body; copy it back onto the conversation summary so an
  // inbound row previews with its text instead of a blank "Name:" (#788).
  // Guarded on lastMessage.id (skip if a newer message has taken over the
  // preview) and on content (a still-undecryptable row keeps its blank
  // placeholder rather than being forced to '').
  syncLastMessagePreview: (conversationId, messageId) => {
    const healed = get().messages[conversationId]?.find((m) => m.id === messageId);
    if (!healed || healed.content == null) return;
    const content = healed.content;
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === conversationId && c.lastMessage?.id === messageId
          ? { ...c, lastMessage: { ...c.lastMessage, content } }
          : c
      ),
    }));
  },

  // Update message reactions
  updateMessageReactions: (messageId, reactions) => {
    const state = get();
    const newMessages: Record<string, Message[]> = {};

    for (const [convId, msgs] of Object.entries(state.messages)) {
      newMessages[convId] = msgs.map((m) =>
        m.id === messageId ? { ...m, reactions } : m
      );
    }

    set({ messages: newMessages });
  },

  // Add reaction to a message
  addReaction: async (messageId, emoji) => {
    const result = await api.addReaction(messageId, emoji);

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    return true;
  },

  // Remove reaction from a message
  removeReaction: async (messageId, emoji) => {
    const result = await api.removeReaction(messageId, emoji);

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    return true;
  },

  // Create a new conversation
  createConversation: async (type, memberIds, name) => {
    const result = await api.createConversation({
      type,
      memberIds,
      name,
    });

    if (result.error) {
      set({ error: result.error.message });
      return null;
    }

    if (result.data) {
      // Refresh conversations list
      await get().fetchConversations();
      return result.data.conversationId;
    }

    return null;
  },

  // Pin / unpin a conversation for the current user. Optimistic: flip the
  // local flag immediately (the list re-sorts pinned-first), revert on error.
  pinConversation: async (conversationId, pinned) => {
    const prev = get().conversations;
    set({
      conversations: prev.map((c) =>
        c.id === conversationId ? { ...c, isPinned: pinned } : c
      ),
    });

    const result = await api.pinConversation(conversationId, pinned);
    if (result.error) {
      set({ conversations: prev, error: result.error.message });
    }
  },

  // Mute / unmute a conversation for the current user. Optimistic, reverting on
  // error. mutedUntil mirrors the backend rule: null unmutes, 'forever' parks
  // far in the future, a duration adds to now.
  muteConversation: async (conversationId, duration) => {
    const prev = get().conversations;
    let mutedUntil: string | null = null;
    if (duration === 'forever') {
      mutedUntil = new Date('9999-12-31T23:59:59.000Z').toISOString();
    } else if (duration) {
      const ms =
        duration === '8h' ? 8 * 3600_000 :
        duration === '1d' ? 24 * 3600_000 :
        7 * 24 * 3600_000;
      mutedUntil = new Date(Date.now() + ms).toISOString();
    }
    const isMuted = mutedUntil !== null;

    set({
      conversations: prev.map((c) =>
        c.id === conversationId ? { ...c, isMuted, mutedUntil } : c
      ),
    });

    const result = await api.muteConversation(conversationId, duration);
    if (result.error) {
      set({ conversations: prev, error: result.error.message });
    }
  },

  // Edit a message (call API)
  editMessageAction: async (messageId, newContent) => {
    // Confirm the message exists in the local store before calling the API.
    const state = get();
    let conversationId: string | null = null;

    for (const [convId, msgs] of Object.entries(state.messages)) {
      if (msgs.some((m) => m.id === messageId)) {
        conversationId = convId;
        break;
      }
    }

    if (!conversationId) {
      set({ error: 'Message not found' });
      return false;
    }

    // ADR-012 P6: the client no longer Signal-encrypts. Send the edit as
    // plaintext over TLS; routes.ts detects this single-key { plaintext }
    // shape, same as the send path.
    const result = await api.editMessage(messageId, { plaintext: newContent });

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    // Update local state immediately
    get().updateMessage(messageId, {
      content: newContent,
      editedAt: new Date().toISOString(),
    });

    return true;
  },

  // Delete a message (call API)
  deleteMessageAction: async (messageId, forEveryone) => {
    // Find the message to get conversationId
    const state = get();
    let conversationId: string | null = null;

    for (const [convId, msgs] of Object.entries(state.messages)) {
      if (msgs.some((m) => m.id === messageId)) {
        conversationId = convId;
        break;
      }
    }

    const result = await api.deleteMessage(messageId, forEveryone);

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    // Update local state
    if (conversationId) {
      get().removeMessage(messageId, conversationId);
    }

    return true;
  },

  // Clear error
  clearError: () => { set({ error: null }); },

  // Search highlight
  setHighlightedMessage: (messageId) => { set({ highlightedMessageId: messageId }); },

  // Reset store on logout
  reset: () => { set({
    conversations: [],
    activeConversationId: null,
    messages: {},
    isLoadingConversations: false,
    isLoadingMessages: false,
    hasMoreMessages: {},
    error: null,
    typingUsers: {},
    highlightedMessageId: null,
    onlineUsers: new Set<string>(),
  }); },

  // Update a message (for edits)
  updateMessage: (messageId, updates) => {
    const state = get();
    const newMessages: Record<string, Message[]> = {};

    for (const [convId, msgs] of Object.entries(state.messages)) {
      newMessages[convId] = msgs.map((m) =>
        m.id === messageId ? { ...m, ...updates } : m
      );
    }

    set({ messages: newMessages });
  },

  // Remove a deleted message from view entirely. Deletion leaves no trace: the
  // server already excludes deleted messages from the list, so the optimistic
  // (deleter) and socket (recipient) paths drop it from local state rather than
  // leaving a "This message was deleted" placeholder.
  removeMessage: (messageId, conversationId) => {
    const state = get();
    const msgs = state.messages[conversationId];
    if (!msgs) return;

    set({
      messages: {
        ...state.messages,
        [conversationId]: msgs.filter((m) => m.id !== messageId),
      },
    });
  },

  // Handle reaction event from WebSocket
  handleReactionEvent: ({ messageId, conversationId, userId, emoji, action }) => {
    const state = get();
    const msgs = state.messages[conversationId];
    if (!msgs) return;

    set({
      messages: {
        ...state.messages,
        [conversationId]: msgs.map((m) => {
          if (m.id !== messageId) return m;

          const reactions = [...m.reactions];
          const existingIndex = reactions.findIndex((r) => r.emoji === emoji);

          if (action === 'add') {
            if (existingIndex >= 0) {
              // Add user to existing reaction
              const existing = reactions[existingIndex];
              if (!existing.userIds.includes(userId)) {
                reactions[existingIndex] = {
                  ...existing,
                  userIds: [...existing.userIds, userId],
                  count: existing.count + 1,
                };
              }
            } else {
              // Create new reaction
              reactions.push({ emoji, userIds: [userId], count: 1 });
            }
          } else if (action === 'remove' && existingIndex >= 0) {
            const existing = reactions[existingIndex];
            const newUserIds = existing.userIds.filter((id) => id !== userId);
            if (newUserIds.length === 0) {
              reactions.splice(existingIndex, 1);
            } else {
              reactions[existingIndex] = {
                ...existing,
                userIds: newUserIds,
                count: newUserIds.length,
              };
            }
          }

          return { ...m, reactions };
        }),
      },
    });
  },

  // Typing indicators
  setUserTyping: (conversationId, userId, userName) => {
    const state = get();
    const existing = state.typingUsers[conversationId] || [];

    if (existing.some((u) => u.userId === userId)) return;

    set({
      typingUsers: {
        ...state.typingUsers,
        [conversationId]: [...existing, { userId, userName }],
      },
    });
  },

  setUserStoppedTyping: (conversationId, userId) => {
    const state = get();
    const existing = state.typingUsers[conversationId] || [];

    set({
      typingUsers: {
        ...state.typingUsers,
        [conversationId]: existing.filter((u) => u.userId !== userId),
      },
    });
  },

  // Online status
  setUserOnline: (userId) => {
    set((state) => ({
      onlineUsers: new Set([...state.onlineUsers, userId]),
    }));
  },

  setUserOffline: (userId) => {
    set((state) => {
      const newSet = new Set(state.onlineUsers);
      newSet.delete(userId);
      return { onlineUsers: newSet };
    });
  },

  // Update delivery status for a message
  updateDeliveryStatus: (messageId, status) => {
    const state = get();
    const newMessages: Record<string, Message[]> = {};

    for (const [convId, msgs] of Object.entries(state.messages)) {
      newMessages[convId] = msgs.map((m) =>
        m.id === messageId ? { ...m, deliveryStatus: status } : m
      );
    }

    set({ messages: newMessages });
  },

  // Add read receipt to a message
  addReadReceipt: (messageId, receipt) => {
    const state = get();
    const newMessages: Record<string, Message[]> = {};

    for (const [convId, msgs] of Object.entries(state.messages)) {
      newMessages[convId] = msgs.map((m) => {
        if (m.id !== messageId) return m;

        const existingReceipts = m.readReceipts || [];
        // Don't add duplicate receipts
        if (existingReceipts.some((r) => r.userId === receipt.userId)) {
          return m;
        }

        return {
          ...m,
          readReceipts: [...existingReceipts, receipt],
          deliveryStatus: 'read',
        };
      });
    }

    set({ messages: newMessages });
  },

  // Mark messages as read (call API + socket)
  markMessagesAsRead: async (conversationId, messageIds) => {
    if (messageIds.length === 0) return;

    // Call API
    const result = await api.markMessagesAsRead(conversationId, messageIds);
    if (result.error) {
      return;
    }

    // Also emit via socket for real-time notification
    socketService.markRead(conversationId, messageIds);

    // Clear the conversation's unread badge locally (#794). The per-conversation
    // unreadCount was only ever populated from getConversations, so the
    // ConversationList badge kept its stale count until the next server refetch.
    // Guarded on API success above, so a failed mark-read leaves it untouched.
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === conversationId && c.unreadCount !== 0
          ? { ...c, unreadCount: 0 }
          : c
      ),
    }));
  },

  // Setup socket listeners - returns cleanup function
  setupSocketListeners: () => {
    const cleanups: Array<() => void> = [];

    // New message
    cleanups.push(
      socketService.on('message:new', ({ message }) => {
        // Convert socket message to our local Message type
        // The socket event has a partial message structure
        const socketMsg = message as unknown as {
          id: string;
          conversationId: string;
          senderId: string;
          senderDeviceId?: string | null;
          senderName?: string;
          messageType: string;
          content?: string | null; // Plaintext content for bot messages
          replyToMessageId?: string | null;
          isForwarded: boolean;
          sentAt?: string;
          createdAt?: string;
        };

        const newMessage: Message = {
          id: socketMsg.id,
          conversationId: socketMsg.conversationId,
          senderId: socketMsg.senderId,
          senderDeviceId: socketMsg.senderDeviceId,
          senderName: socketMsg.senderName || 'Unknown',
          senderAvatarUrl: null,
          messageType: socketMsg.messageType,
          content: socketMsg.content || undefined, // Include content for bot messages
          replyToMessageId: socketMsg.replyToMessageId || null,
          replyToPreview: null,
          hasAttachments: false,
          isForwarded: socketMsg.isForwarded,
          createdAt: socketMsg.sentAt || socketMsg.createdAt || new Date().toISOString(),
          editedAt: null,
          deletedAt: null,
          expiresAt: null,
          attachments: [],
          reactions: [],
        };

        // Add message immediately (metadata; content filled below if absent)
        get().addMessage(newMessage);

        // ADR-012 P6: in `server` mode the broadcast carries no content
        // (routes.ts only inlines content in legacy mode). The client no
        // longer Signal-decrypts. When content is absent, refetch the
        // conversation through the same server-decrypt getMessages path P5
        // verified - it returns the new message's plaintext after the
        // server's membership check. Bot / legacy-mode messages arrive with
        // content already and need no refetch.
        if (!socketMsg.content) {
          void get().fetchMessages(socketMsg.conversationId).then(() => {
            // The refetch healed the thread; carry the decrypted body onto the
            // conversation-list preview too (the broadcast left it blank).
            get().syncLastMessagePreview(socketMsg.conversationId, socketMsg.id);
          });
        }
      })
    );

    // Message edited
    cleanups.push(
      socketService.on('message:edited', ({ messageId, conversationId, editedAt }) => {
        get().updateMessage(messageId, { editedAt });
        // The event carries no content - the broadcast expects clients to
        // fetch their own copy. Without this refetch, an open conversation
        // kept showing the pre-edit text marked "edited" until the next full
        // reload. Same server-decrypt heal message:new uses; refetch only
        // when the edited message is actually loaded, so background
        // conversations don't trigger fetches.
        if (get().messages[conversationId]?.some((m) => m.id === messageId)) {
          void get().fetchMessages(conversationId);
        }
      })
    );

    // Message deleted
    cleanups.push(
      socketService.on('message:deleted', ({ messageId, conversationId }) => {
        get().removeMessage(messageId, conversationId);
      })
    );

    // Reaction
    cleanups.push(
      socketService.on('message:reaction', (data) => {
        get().handleReactionEvent(data);
      })
    );

    // Typing
    cleanups.push(
      socketService.on('user:typing', ({ conversationId, userId, userName }) => {
        get().setUserTyping(conversationId, userId, userName);
      })
    );

    cleanups.push(
      socketService.on('user:stopped-typing', ({ conversationId, userId }) => {
        get().setUserStoppedTyping(conversationId, userId);
      })
    );

    // Online status
    cleanups.push(
      socketService.on('user:online', ({ userId }) => {
        get().setUserOnline(userId);
      })
    );

    cleanups.push(
      socketService.on('user:offline', ({ userId }) => {
        get().setUserOffline(userId);
      })
    );

    // Conversation events
    cleanups.push(
      socketService.on('conversation:created', () => {
        // Refresh conversations list when a new one is created
        void get().fetchConversations();
      })
    );

    cleanups.push(
      socketService.on('conversation:updated', () => {
        // Refresh conversations to get updated info
        void get().fetchConversations();
      })
    );

    cleanups.push(
      socketService.on('conversation:member-added', () => {
        void get().fetchConversations();
      })
    );

    cleanups.push(
      socketService.on('conversation:member-left', () => {
        void get().fetchConversations();
      })
    );

    // Read receipts
    cleanups.push(
      socketService.on('message:read-receipt', ({ messageId, userId, userName, readAt }) => {
        get().addReadReceipt(messageId, { userId, userName, readAt });
      })
    );

    // Return cleanup function
    return () => {
      cleanups.forEach((cleanup) => { cleanup(); });
    };
  },
}));
